import React, { useState, useRef, useEffect } from "react"
import PropTypes from "prop-types"
import {
  Container,
  List,
  ListItem,
  MessagesArea,
  Links,
  Link,
  Messages,
  Message,
  Bubble,
  InputArea,
  MessageArea,
  Counter,
  Error,
  Banner,
  BannerText
} from "./styles"
import MessageField from "./text-area"
import Grid from "../grid"
import { P } from "../typography"
import Moment from "../moment"
import Button from "../button"
import Avatar from "../avatar"

const Conversations = ({
  timezone,
  conversations,
  selected,
  selectedHandler,
  profileHandler,
  shouldShowProfileHandler,
  submitHandler,
  endChatHandler,
  currentUserType,
  openRescheduleModal,
  actionsSelect
}) => {
  const messagesContainer = useRef()
  const [message, setMessage] = useState("")
  const id = selected?.id
  const [submitting, setSubmitting] = useState(false)
  const [typing, setTyping] = useState(false)

  useEffect(() => {
    if (id && messagesContainer.current) {
      messagesContainer.current.scrollTop = messagesContainer.current.scrollHeight
      messagesContainer.current.style.visibility = "visible"
    }
  }, [id])

  const selectConversation = (event, conversation) => {
    event.preventDefault()
    if (selectedHandler !== undefined) selectedHandler(conversation)
    setMessage("")
    setTyping(false)
  }

  const sendMessage = (event) => {
    const cleanedMessage = cleanMessage(message)
    const contentPresent = validateMessage(cleanMessage(message))
    if (!contentPresent) {
      alert("Please enter a message")
      return
    }

    if (event) event.preventDefault()
    if (submitHandler !== undefined) {
      setSubmitting(true)
      Promise.resolve(submitHandler(selected, cleanedMessage)).then((result) => {
        if (result && result.error) {
          setSubmitting(false)
          return
        }

        setMessage("")
        setTyping(false)
        if (messagesContainer.current) messagesContainer.current.scrollTop = messagesContainer.current.scrollHeight
        setSubmitting(false)
      })
    } else {
      alert("Please enter a message")
      setMessage("")
      setTyping(false)
    }
  }

  const validateMessage = (messageToValidate) => {
    return messageToValidate.length > 0
  }

  const cleanMessage = (messageToClean) => {
    return messageToClean.trim().replace(/\n{3,}/g, "\n")
  }

  const handleMessageChange = (event) => {
    setMessage(event.target.value)
    displayMessagePrompt(event.target.value)
  }

  const displayMessagePrompt = (value) => {
    setTyping(value.length > 0)
  }

  const checkSubmit = (event) => {
    const enterPressed = event.key === "Enter"
    const shiftPressed = event.shiftKey
    const contentPresent = validateMessage(cleanMessage(message))
    const goodToGo = enterPressed && !shiftPressed && contentPresent
    const submittingEmptyMessage = enterPressed && !shiftPressed && !contentPresent

    if (goodToGo) {
      sendMessage()
    } else if (submittingEmptyMessage) {
      event.preventDefault()
      alert("Please enter a message")
      setMessage("")
      setTyping(false)
    }
  }

  const viewProfile = (event) => {
    event.preventDefault()
    if (profileHandler !== undefined) profileHandler(selected)
  }

  const endChat = (event) => {
    event.preventDefault()
    if (endChatHandler !== undefined) endChatHandler(selected)
    setMessage("")
    setTyping(false)
  }

  const archivedMessage = (selected) => {
    let msg = ""

    if (selected.archived_by_name) {
      msg = `${selected.archived_by_name} ended the conversation.`
    } else {
      msg = `Conversation ended by placement status update.`
    }

    if (selected.archived_by_current_user && selected.conversation_enabled) {
      msg = `${msg} Send a message to reopen the conversation.`
    }

    return msg
  }

  const backToMessages = (event) => {
    event.preventDefault()
    if (selectedHandler !== undefined) selectedHandler(null)
    setMessage("")
    setTyping(false)
  }

  const rescheduleInterviewEnabled =
    selected?.employer_interview?.reschedule_interview_enabled &&
    !!selected?.employer_interview?.interview_start_date &&
    openRescheduleModal !== undefined

  return (
    <Container>
      <List show={!selected}>
        {conversations.map((conversation) => (
          <ListItem
            key={conversation.id}
            selected={conversation.id === selected?.id}
            onClick={(event) => selectConversation(event, conversation)}
          >
            <Grid grid="auto / auto-flow" gap="1rem" placeItems="center" mobile={true}>
              <Avatar
                avatarExists={conversation.avatar_exists}
                src={conversation.avatar}
                size="small"
                firstName={conversation.first_name}
                lastName={conversation.last_name}
              />
              <div style={{ maxWidth: "100%", overflow: "hidden" }}>
                <P
                  variant="secondary"
                  marginless
                  bold
                  style={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {conversation.title}
                </P>
                <P
                  variant="secondary"
                  marginless
                  style={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {conversation.last_message_snippet || "New conversation"}
                </P>
              </div>
            </Grid>
            <Grid gap="0.25rem" placeItems="center end" mobile={true}>
              <P variant="secondary" marginless nowrap>
                {conversation.last_message_time}
              </P>
              <div>
                {selected?.id !== conversation.id && conversation.new_messages_counter > 0 && (
                  <Counter>{conversation.new_messages_counter}</Counter>
                )}
              </div>
            </Grid>
          </ListItem>
        ))}
      </List>
      <MessagesArea show={selected} selected={selected}>
        {selected ? (
          <>
            <Links rescheduleInterviewEnabled={rescheduleInterviewEnabled}>
              <div>
                {selectedHandler !== undefined && (
                  <Link onClick={backToMessages} className="back-to-messages">
                    Back to Messages
                  </Link>
                )}
              </div>
              <div>
                {!!actionsSelect ? (
                  actionsSelect
                ) : (
                  <>
                    {profileHandler !== undefined &&
                      (shouldShowProfileHandler ? shouldShowProfileHandler(selected) : false) && (
                        <Link onClick={viewProfile}>
                          View {currentUserType == "employer" ? selected.participant_name : selected.employer_name}'s
                          Profile
                        </Link>
                      )}
                    {endChatHandler !== undefined && !selected.archived && id !== "new" && (
                      <Link onClick={endChat}>End Chat</Link>
                    )}
                  </>
                )}
              </div>
            </Links>
            {rescheduleInterviewEnabled && (
              <Banner>
                <BannerText>
                  You have an upcoming interview with {selected.participant_name} taking place on{" "}
                  <strong>
                    <Moment
                      value={selected.employer_interview.interview_start_date}
                      format="D MMMM YYYY [at] HH:mm"
                      tz={timezone}
                    />
                  </strong>{" "}
                  -{" "}
                  <Link
                    onClick={() => openRescheduleModal(selected)}
                    style={{ width: "fit-content", display: "inline-block" }}
                  >
                    Need to Reschedule?
                  </Link>
                </BannerText>
              </Banner>
            )}
            <Messages key={selected?.id} ref={messagesContainer}>
              {selected.conversation_messages.length > 0 &&
                selected.conversation_messages.map((message) =>
                  !!message.me ? (
                    <Message key={message.id} align="end">
                      <Bubble variant="primary">
                        <P variant="white" marginless bold>
                          {message.fromUserName}
                        </P>
                        <P variant="white" marginless>
                          {message.text}
                        </P>
                      </Bubble>
                      <P variant="secondary" size="small" marginless align="right">
                        {!!message.sender_name && `${message.sender_name} ·`}{" "}
                        <Moment value={message.created_at} format="MMM DD · HH:mm" tz={timezone} />
                      </P>
                    </Message>
                  ) : (
                    <Message key={message.id} align="start">
                      <Bubble>
                        <P variant="secondary" marginless bold>
                          {message.fromUserName}
                        </P>
                        <P variant="secondary" marginless>
                          {message.text}
                        </P>
                      </Bubble>
                      <P variant="secondary" size="small" marginless align="left">
                        {!!message.sender_name && `${message.sender_name} ·`}{" "}
                        <Moment value={message.created_at} format="MMM DD · HH:mm" tz={timezone} />
                      </P>
                    </Message>
                  )
                )}
            </Messages>
            <InputArea>
              <MessageArea>
                <MessageField
                  name="message"
                  placeholder="Type a message"
                  value={message}
                  onChange={handleMessageChange}
                  onKeyPress={checkSubmit}
                  rows={1}
                  disabled={
                    (selected.archived && (!selected.archived_by_current_user || !selected.conversation_enabled)) ||
                    submitting
                  }
                />
                <P
                  style={{ visibility: typing ? "visible" : "hidden" }}
                  variant="secondary"
                  size="small"
                  marginless
                  align="right"
                >
                  <strong>Shift + Return</strong> to add a new line
                </P>
              </MessageArea>

              <Button
                style={{ height: "51px" }}
                variant="success"
                onClick={sendMessage}
                disabled={message.length === 0 || submitting}
              >
                Send
              </Button>
            </InputArea>
            {selected.archived && <Error>{archivedMessage(selected)}</Error>}
          </>
        ) : (
          <P variant="secondary" size="large" marginless>
            Select a conversation
          </P>
        )}
      </MessagesArea>
    </Container>
  )
}

Conversations.propTypes = {
  timezone: PropTypes.string,
  conversations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      avatar: PropTypes.string,
      avatarExists: PropTypes.bool,
      last_message_time: PropTypes.string,
      last_message_snippet: PropTypes.string,
      new_messages_counter: PropTypes.number,
      conversation_messages: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          text: PropTypes.string,
          created_at: PropTypes.string
        })
      )
    })
  ),
  selectedHandler: PropTypes.func,
  shouldShowProfileHandler: PropTypes.func,
  profileHandler: PropTypes.func,
  submitHandler: PropTypes.func,
  endChatHandler: PropTypes.func,
  currentUserType: PropTypes.string.isRequired
}

Conversations.defaultProps = {
  conversations: [],
  selectedHandler: undefined,
  shouldShowProfileHandler: undefined,
  profileHandler: undefined,
  submitHandler: undefined,
  endChatHandler: undefined,
  currentUserType: "participant"
}

export default Conversations
